import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Drawer } from '@mui/material';
import queryString from 'query-string';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { TABS } from '../../consts';
import { useNewOrderContext } from '../../context/NewOrderContext';
import { useSystemContext } from '../../context/SystemContext/SystemContext';
import { useUserMenuContext } from '../../context/UserMenuContext';
import SeatContent from '../../pages/SeatInfo/components/SeatContent';
import { getPaymentNoticeAlerts } from '../../utils/cancellationAlert';
import { fetchGameDayData, generateTimeSlots } from '../../utils/timeSlots';
import Button from '../Button';
import GiftInfoModal from '../GiftInfoModal/GiftInfoModal';
import PaymentNoticeDialog from '../PaymentNoticeDialog/PaymentNoticeDialog';
import Select from '../Select';

interface OrderConfirmationDrawerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (param: { deliveryRequest: string; isSendAsGift: boolean; giftInfo?: any }) => void | Promise<void>;
  preOrderSetup?: boolean;
}

type OrderConfirmationPayload = {
  deliveryRequest: string;
  isSendAsGift: boolean;
  giftInfo?: any;
};

const OrderConfirmationDrawer: React.FC<OrderConfirmationDrawerProps> = ({ open, onClose, onConfirm, preOrderSetup = false }) => {
  const { t } = useTranslation();
  const { isPreOrder, seatInfo, stadiumInfo } = useSystemContext();
  const { orderDetails } = useNewOrderContext();
  const { listRestaurantData, listRestaurantWithFood } = useUserMenuContext();

  const READY = useMemo(() => <span className="font-bold">{t('WHEN_ITS_READY')}</span>, [t]);

  const [deliveryRequest, setDeliveryRequest] = useState<string>(orderDetails.delivery_request || 'WHEN_ITS_READY');
  const [isSendAsGift, setIsSendAsGift] = useState(false);
  const [openGiftInfoModal, setOpenGiftInfoModal] = useState(false);
  const [openPaymentNotice, setOpenPaymentNotice] = useState(false);
  const [isSubmittingPaymentNotice, setIsSubmittingPaymentNotice] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<OrderConfirmationPayload | null>(null);
  const [timeSlotOptions, setTimeSlotOptions] = useState<{ value: string; label: string | ReactNode }[]>([{ value: 'WHEN_ITS_READY', label: READY }]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const paymentNoticeAlerts = useMemo(
    () =>
      getPaymentNoticeAlerts({
        order: orderDetails,
        restaurants: [...listRestaurantWithFood, ...listRestaurantData],
        language: stadiumInfo?.language,
      }),
    [listRestaurantData, listRestaurantWithFood, orderDetails, stadiumInfo?.language]
  );

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const effectiveDeliveryRequest =
    deliveryRequest && timeSlotOptions.some((option) => option.value === deliveryRequest)
      ? deliveryRequest
      : timeSlotOptions[0]?.value || '';

  useEffect(() => {
    if (open && preOrderSetup) {
      setDeliveryRequest(orderDetails.delivery_request === 'WHEN_ITS_READY' ? '' : orderDetails.delivery_request || '');
    }
  }, [open, preOrderSetup, orderDetails.delivery_request]);

  useEffect(() => {
    if (!open || !preOrderSetup || timeSlotOptions.length === 0) return;
    if (!deliveryRequest || !timeSlotOptions.some((option) => option.value === deliveryRequest)) {
      setDeliveryRequest(timeSlotOptions[0].value);
    }
  }, [deliveryRequest, open, preOrderSetup, timeSlotOptions]);

  useEffect(() => {
    const loadTimeSlots = async () => {
      if (!seatInfo?.order_date || !stadiumInfo?.id) {
        setTimeSlotOptions(preOrderSetup ? [] : [{ value: 'WHEN_ITS_READY', label: READY }]);
        return;
      }
      const shouldUsePreOrderSlots = preOrderSetup || isPreOrder;

      if (isPreOrder && !preOrderSetup) {
        return;
      }

      setIsLoadingTimeSlots(true);
      try {
        const gameDayData = await fetchGameDayData(stadiumInfo.id, seatInfo.order_date);

        if (!gameDayData) {
          console.warn('No game day data found, using default option');
          setTimeSlotOptions(preOrderSetup ? [] : [{ value: 'WHEN_ITS_READY', label: READY }]);
          return;
        }

        const preOrderRules = stadiumInfo.pre_order_rules?.preorder_rules;
        const restaurantPreOrderRules = [...listRestaurantWithFood, ...listRestaurantData]
          .map((restaurant) => (restaurant as any).preorder_rules)
          .filter(Boolean);

        const slots = generateTimeSlots(gameDayData, preOrderRules, shouldUsePreOrderSlots, restaurantPreOrderRules);
        const allOptions = preOrderSetup ? slots : [{ value: 'WHEN_ITS_READY', label: READY }, ...slots];

        setTimeSlotOptions(allOptions);
        setDeliveryRequest((current) => (current && allOptions.some((option) => option.value === current) ? current : allOptions[0]?.value || ''));
      } catch (error) {
        console.error('Error loading time slots:', error);
        setTimeSlotOptions(preOrderSetup ? [] : [{ value: 'WHEN_ITS_READY', label: READY }]);
      } finally {
        setIsLoadingTimeSlots(false);
      }
    };

    loadTimeSlots();
  }, [READY, isPreOrder, listRestaurantData, listRestaurantWithFood, preOrderSetup, seatInfo?.order_date, stadiumInfo?.id, stadiumInfo?.pre_order_rules]);

  const showPaymentNotice = (payload: OrderConfirmationPayload) => {
    if (!isPreOrder || paymentNoticeAlerts.length === 0) {
      onConfirm(payload);
      onClose();
      return;
    }

    setPendingPayload(payload);
    setOpenPaymentNotice(true);
  };

  const handleOrder = () => {
    const selectedDeliveryRequest = isPreOrder ? orderDetails.delivery_request || deliveryRequest : deliveryRequest;

    if (preOrderSetup) {
      const setupDeliveryRequest = effectiveDeliveryRequest;
      if (!setupDeliveryRequest) return;

      onConfirm({ deliveryRequest: setupDeliveryRequest, isSendAsGift: false });
      onClose();
      return;
    }

    if (isSendAsGift) {
      setOpenGiftInfoModal(true);
    } else {
      showPaymentNotice({ deliveryRequest: selectedDeliveryRequest, isSendAsGift: false });
    }
  };

  const handleGiftInfoConfirm = (giftInfo: any) => {
    setOpenGiftInfoModal(false);
    showPaymentNotice({ deliveryRequest: isPreOrder ? orderDetails.delivery_request || deliveryRequest : deliveryRequest, isSendAsGift: true, giftInfo });
  };

  const handleSubmitPaymentNotice = async () => {
    if (!pendingPayload || isSubmittingPaymentNotice) return;

    setIsSubmittingPaymentNotice(true);
    try {
      await onConfirm(pendingPayload);
      setPendingPayload(null);
      setOpenPaymentNotice(false);
      onClose();
    } finally {
      setIsSubmittingPaymentNotice(false);
    }
  };

  const handleCancelPaymentNotice = () => {
    if (isSubmittingPaymentNotice) return;
    setPendingPayload(null);
    setOpenPaymentNotice(false);
  };

  const onChangeData = () => {
    const seatTabLink = queryString.stringifyUrl({
      url: `/neworder`,
      query: { ...queryParams, tab: TABS.SEAT },
    });

    onClose();
    navigate(seatTabLink);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            maxWidth: '672px',
            height: '100dvh',
          },
        }}
      >
        <div className="h-[100dvh] bg-gray-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-4 py-1 px-4 border-b border-gray-200">
            <button onClick={onClose} className="p-3 flex items-center justify-center bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all">
              <ArrowBackIcon sx={{ color: 'black', width: '20px', height: '20px' }} />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 flex-1 text-center">{t('ORDER_CONFIRMATION')}</h2>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-auto">
            <div className="flex-1 p-4 flex flex-col gap-4 overflow-auto">
              {(!isPreOrder || preOrderSetup) && (
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('DELIVERY_REQUEST')}</label>
                  <Select
                    value={effectiveDeliveryRequest}
                    onChange={(e) => {
                      setDeliveryRequest(e.target.value as string);
                    }}
                    options={timeSlotOptions}
                    disabled={isLoadingTimeSlots}
                  />
                </div>
              )}
              <SeatContent
                data={seatInfo!}
                isPreOrder={isPreOrder}
                dataFieldBgColor="bg-[#EAECF0]"
                deliveryRequest={isPreOrder && !preOrderSetup ? orderDetails.delivery_request : undefined}
              />
              <div className="pt-6">
                <Button type="light" onClick={onChangeData}>
                  {t('CHANGE_SEAT_INFO')}
                </Button>
              </div>
            </div>

            <div className="bg-white border-t border-gray-200 p-4 max-w-[672px] mx-auto w-full">
              {isPreOrder && !preOrderSetup && (
                <div className="flex items-center gap-2 mb-4">
                  <input
                    className={`w-4 h-4 border-2 rounded-md focus:outline-none border-gray-300`}
                    type="checkbox"
                    id="terms_and_conditions"
                    checked={isSendAsGift}
                    onChange={(e) => setIsSendAsGift(e.target.checked)}
                  />
                  <p className={`text-sm not-italic font-normal leading-5 text-black-600`}>{t('SEND_AS_GIFT')}</p>
                </div>
              )}
              <Button type="secondary" onClick={handleOrder} disabled={preOrderSetup && (isLoadingTimeSlots || !effectiveDeliveryRequest)}>
                {preOrderSetup ? t('CONFIRM') : t('ORDER_CONFIRMATION')}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      <GiftInfoModal
        open={openGiftInfoModal}
        onClose={() => setOpenGiftInfoModal(false)}
        onConfirm={handleGiftInfoConfirm}
        deliveryRequest={isPreOrder ? orderDetails.delivery_request || deliveryRequest : deliveryRequest}
      />
      <PaymentNoticeDialog
        open={openPaymentNotice}
        onCancel={handleCancelPaymentNotice}
        onSubmit={handleSubmitPaymentNotice}
        isSubmitting={isSubmittingPaymentNotice}
        alerts={paymentNoticeAlerts}
      />
    </>
  );
};

export default OrderConfirmationDrawer;
